import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { css } from '@emotion/react';

import { infoCenterLocales } from 'locale/locale.config';

import { cms } from 'lib/cms';

import { mobileOnly, tabletUp } from 'styles/utils';

import PageNotFound from 'pages/404';

import { promosBaseCss } from 'components/shared/PromoCards/PromoCards.style';
import PromoCard from 'components/shared/Promo/PromoCard';
import Text from 'components/shared/Text/Text';
import Container from 'components/shared/Container/Container';
import {
  articleShape,
  menuItemShape,
} from 'components/InfoCenter/InfoCenter.config';
import PageLayout from 'components/shared/PageLayout/PageLayout';
import NavbarSpacer from 'components/shared/Navbar/NavbarSpacer';
import Breakpoint from 'components/shared/Breakpoint/Breakpoint';
import InfoCenterNavbar from 'components/InfoCenter/InfoCenterNavbar';
import PostBreadcrumbs from 'components/shared/PostBreadcrums/PostBreadcrums';
import PostHead from 'components/shared/PostHead/PostHead';
import PostLayout from 'components/shared/PostLayout/PostLayout';
import BottomCta from 'components/shared/BottomCta/BottomCta';

const promosAsideCss = css`
  ${mobileOnly} {
    margin-top: 30px;
  }

  ${tabletUp} {
    margin-top: 59px;
  }
`;

const promosTitleCss = css`
  &.MuiTypography-root {
    margin-bottom: 24px;
  }
`;

const bottomCtaCss = css`
  margin-top: 140px;
`;

function Article({ article, promos, topMenuItems }) {
  const router = useRouter();

  if (router.isFallback) return null;

  if (!article) return <PageNotFound />;

  return (
    <>
      <PostHead post={article} />
      <PageLayout bottomPadding={0}>
        <NavbarSpacer />
        {/* Navbar */}
        <Breakpoint tablet desktop>
          <InfoCenterNavbar
            currentSlug={article.subcategory?.category?.slug}
            menuItems={topMenuItems}
          />
        </Breakpoint>

        {/* Breadcrumbs */}
        <PostBreadcrumbs
          breadcrumbs={[
            article.subcategory.category,
            article.subcategory,
            article,
          ]}
        />

        {/* Post layout */}
        <PostLayout {...article} />

        {/* Promos */}
        <Container>
          {promos.length > 0 && (
            <aside css={promosAsideCss}>
              <Text fontSize={2.4} fontWeight={500} css={promosTitleCss}>
                You may also find interest in these videos & articles
              </Text>
              <div css={promosBaseCss}>
                {promos.map(article => (
                  <PromoCard key={article.slug} {...article} />
                ))}
              </div>
            </aside>
          )}
        </Container>

        <BottomCta css={bottomCtaCss} />
      </PageLayout>
    </>
  );
}
export async function getStaticPaths() {
  /**
   * NOTE: Setting a limit of 1,000 articles.
   * This might have an impact on build time.
   * We're not sure yet and we should pay attention to it.
   */
  const articles = await cms.getAllArticles(1000);

  return {
    paths: infoCenterLocales.reduce((acc, localeCode) => {
      const articlesByLocale = articles.map(article => ({
        params: {
          locale: localeCode,
          article: article.slug,
          subcategory: article.subcategory.slug,
          category: article.subcategory.category.slug,
        },
      }));

      return [...acc, ...articlesByLocale];
    }, []),
    fallback: true,
  };
}

export async function getStaticProps({ params }) {
  const pageData = await Promise.all([
    cms.getArticle(params.article, params.subcategory, params.category),
    cms.getArticlePromosBySubcategory(params.subcategory, params.article),
    cms.getInfoCenterTopMenu(),
  ]);

  const article = pageData[0];
  const promos = pageData[1];
  const topMenuItems = pageData[2];

  return {
    props: { article, promos, topMenuItems },
  };
}

Article.propTypes = {
  article: PropTypes.shape(articleShape),
  promos: PropTypes.arrayOf(PropTypes.shape(articleShape)),
  topMenuItems: PropTypes.arrayOf(PropTypes.shape(menuItemShape)),
};

export default Article;
